// Cohorts and dates (AdminRecordings.dc.html's second tab): a card per intake or group, its sessions
// with their state, the Accelerator groups' WhatsApp link, and Add cohort or Add a session. Not on the
// board, as the map and the critic settled: pressing a session still to come opens a small box for its
// date, UK time, length and Zoom link (owner and admin), and pressing a held one opens its register
// (owner, admin, support, and that product's host). The board's calendar line is dropped (Adam's
// answer 9), and so is any date-change email (Adam, 5 Oct, stage 6): members see the new date in the
// hub, and the footnote says nothing is emailed. A mastermind's groups (the mastermind build, undrawn): owner
// and admin rename a group in its head, remove 1 nobody's in from its foot, and add a group or a session for
// every group under the product's last card (src/admin/mastermind/Groups.jsx).
// A product made in Add a product with nothing on it yet (the product-types plan, 13 Oct): a live course with
// its weeks but no intake gets a card of its own with Add cohort 1, and a mastermind with no group gets its
// groups' foot (Add group 1, Add a session for every group). Owner and admin only.
// The Accelerator critique (admin 7, 20 Oct): each card has the anchor #cohort-<id> (Today's Live today opens a
// group's sessions there); a held row says how far its register has got, with an arrow ("Register 4 of 5", "No
// register yet", or for 1 held more than 14 days ago "4 marked"); a mastermind group's card offers the group's
// latest Zoom link to its later sessions that have none, in 1 write; and a group's next session comes with the last
// 1's length and Zoom link, its time offered from the last dated 1.
import { Fragment, useEffect, useId, useState } from 'react'
import { Link } from '../../lib/router.jsx'
import { Button, DateField, Dot, EmptyState, Field, Select, Switch, TextInput, TimeField, adminPath, ukToIso, ukDayOf } from '../ui/index.js'
import { uk, longDate, time } from '../../lib/format.js'
import { COPY } from './copy.js'
import { buildCohortCards, lengthOptions } from './model.js'
import {
  saveSession, addCohort, addGroupSession, saveWhatsapp, saveDealSurgery, loadRegisterCounts, RECENT_MS, zoomFillFor, fillZoomLinks,
} from './data.js'
// the Accelerator's groups: rename, remove, add a group and a session for every group (the mastermind build)
import { RenameButton, RenameRow, RemoveGroup, GroupsFoot } from '../mastermind/Groups.jsx'
import { addEverySession } from '../mastermind/data.js'

const OA = role => role === 'owner' || role === 'admin'
// the products whose intakes have a deal surgery (Profit On Your Terms's own home draws it)
const SURGERY_SLUGS = ['poyt']
const hhmm = iso => { if (!iso) return ''; const u = uk(iso); return `${String(u.hh).padStart(2, '0')}:${String(u.mm).padStart(2, '0')}` }

// A held row's words for how far its register has got: { text, todo } (todo: someone's still to mark, in ink).
// count: { marked, total } (total null for 1 held more than 14 days ago), or nothing yet: Held.
export function heldWords(count) {
  if (!count) return { text: COPY.states.held, todo: false }
  if (count.total == null) return { text: count.marked ? COPY.markedOnly(count.marked) : COPY.states.held, todo: false }
  if (!count.total) return { text: COPY.states.held, todo: false }
  if (!count.marked) return { text: COPY.noRegisterYet, todo: true }
  return { text: COPY.registerOf(count.marked, count.total), todo: count.marked < count.total }
}

// the arrow on a row that opens its register
const Arrow = () => (
  <svg className="adc-row-go" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
    <path d="M4.5 2.5 8 6l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export function DatesTab({ data, reload, api, role, personId }) {
  const nowMs = Date.now()
  const cards = buildCohortCards(data, nowMs)
  const [open, setOpen] = useState(null)
  const [added, setAdded] = useState({})
  const [lines, setLines] = useState({})
  const [busy, setBusy] = useState(null)
  const [renaming, setRenaming] = useState(null)
  const [groupLine, setGroupLine] = useState(null)
  const edits = OA(role)
  // a mastermind's group cards (not its every-group card), and the last card of each mastermind
  const isGroup = card => card.product.type === 'mastermind' && Boolean(card.cohort?.id)
  const lastOf = new Map(cards.filter(c => c.product.type === 'mastermind').map(c => [c.product.id, c.key]))

  // how far each held session's register has got, read again whenever the held sessions change
  const held = cards.flatMap(c => c.rows).filter(r => r.state === 'held').map(r => r.session)
  const heldKey = held.map(h => h.id).sort().join(',')
  const [counts, setCounts] = useState(null)
  useEffect(() => {
    let alive = true
    loadRegisterCounts(api, held, Date.now()).then(c => { if (alive) setCounts(c) }, () => {})
    return () => { alive = false }
    // the held sessions by their ids: a reload that changes nothing held reads nothing again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, heldKey])

  async function add(card) {
    if (busy) return
    setBusy(card.key)
    setLines(l => ({ ...l, [card.key]: null }))
    try {
      if (card.add.kind === 'cohort') await addCohort(api, { product: card.product, data, personId })
      else if (card.add.kind === 'every') await addEverySession(api, { product: card.product, sessions: data.sessions, personId })
      else await addGroupSession(api, { product: card.product, cohort: card.cohort, data, personId })
      await reload()
      setAdded(a => ({ ...a, [card.add.kind === 'cohort' ? card.product.id : card.key]: true }))
    } catch (e) {
      setLines(l => ({ ...l, [card.key]: { bad: true, text: api.words(e) } }))
    } finally {
      setBusy(null)
    }
  }

  if (!cards.length) return <EmptyState>{COPY.noCohorts}</EmptyState>
  return (
    <>
      <div className="adc-cards">
        {cards.map(card => {
          const addedHere = card.add && (card.add.kind === 'cohort' ? added[card.product.id] : added[card.key])
          if (card.foot) {
            return (
              <Fragment key={card.key}>
                {edits && card.add && (
                  <section className="ad-card adc-cohort adc-cohort--first" aria-label={`${card.product.name}, ${card.add.label}`}>
                    <div className="adc-cohort-head">
                      <span className="adc-cohort-names">
                        <span className="adc-cohort-product"><Dot product={card.product} />{card.product.name}</span>
                      </span>
                    </div>
                    <p className="adc-first-line">{COPY.firstCohortLine}</p>
                    <div className="adc-cohort-foot">
                      <Button size="sm" onClick={() => add(card)} busy={busy === card.key} disabled={Boolean(addedHere) || Boolean(busy)}>
                        {addedHere ? COPY.added : card.add.label}
                      </Button>
                      {lines[card.key] && <span className={lines[card.key].bad ? 'ad-error' : 'adc-flash'} role={lines[card.key].bad ? 'alert' : 'status'}>{lines[card.key].text}</span>}
                    </div>
                  </section>
                )}
                {edits && lastOf.get(card.product.id) === card.key && (
                  <GroupsFoot product={card.product} data={data} api={api} personId={personId} reload={reload} line={groupLine} onLine={setGroupLine} />
                )}
              </Fragment>
            )
          }
          return (
            <Fragment key={card.key}>
            <section className="ad-card adc-cohort" aria-label={`${card.product.name}, ${card.cohort.name}`} id={card.cohort.id ? `cohort-${card.cohort.id}` : undefined}>
              <div className="adc-cohort-head">
                <span className="adc-cohort-names">
                  <span className="adc-cohort-product"><Dot product={card.product} />{card.product.name}</span>
                  <span className="adc-cohort-name">{card.cohort.name}</span>
                </span>
                <span className="amm-group-tools">
                  {card.pattern && <span className="adc-cohort-pattern">{card.pattern}</span>}
                  {edits && isGroup(card) && (
                    <RenameButton card={card} open={renaming === card.cohort.id} onToggle={() => setRenaming(r => (r === card.cohort.id ? null : card.cohort.id))} />
                  )}
                </span>
              </div>
              {edits && isGroup(card) && renaming === card.cohort.id && (
                <RenameRow card={card} api={api} reload={reload} onCancel={() => setRenaming(null)}
                  onDone={text => { setRenaming(null); setLines(l => ({ ...l, [card.key]: { text } })) }} />
              )}
              <div className="adc-rows">
                {card.rows.map(row => {
                  const held = row.state === 'held'
                  const can = held || (edits && row.state !== 'cancelled')
                  const recent = held && Date.parse(row.session.starts_at) > nowMs - RECENT_MS
                  const count = held && counts ? counts[row.session.id] : null
                  const words = held ? heldWords(count && (recent ? count : { marked: count.marked, total: null })) : null
                  const inner = (
                    <>
                      <span className="adc-row-label">{row.label}</span>
                      <span className="adc-row-date">{row.date}</span>
                      <span className="adc-row-time">{row.time}</span>
                      {held
                        ? <span className={`adc-row-state adc-row-state--held${words.todo ? ' adc-row-state--todo' : ''}`}>{words.text}<Arrow /></span>
                        : <span className={`adc-row-state adc-row-state--${row.state}`}>{COPY.states[row.state]}</span>}
                    </>
                  )
                  return (
                    <div key={row.session.id} className="adc-row-wrap">
                      {held && <Link to={adminPath.register(row.session.id)} className="adc-row adc-row--link" aria-label={COPY.openRegister(row.label, row.date)}>{inner}</Link>}
                      {!held && can && (
                        <button type="button" className="adc-row adc-row--link" aria-expanded={open === row.session.id}
                          aria-label={COPY.editSession(row.label, row.date)} onClick={() => setOpen(o => (o === row.session.id ? null : row.session.id))}>
                          {inner}
                        </button>
                      )}
                      {!can && <div className="adc-row">{inner}</div>}
                      {open === row.session.id && (
                        <SessionBox row={row} card={card} api={api} personId={personId}
                          onClose={() => setOpen(null)}
                          onSaved={async text => { await reload(); setOpen(null); setLines(l => ({ ...l, [card.key]: { text } })) }} />
                      )}
                    </div>
                  )
                })}
              </div>
              {card.link && <WhatsappBox card={card} api={api} personId={personId} edits={edits} reload={reload} />}
              {edits && isGroup(card) && zoomFillFor(data.sessions, card.cohort.id, nowMs) && (
                <ZoomFillBox card={card} fill={zoomFillFor(data.sessions, card.cohort.id, nowMs)} api={api} personId={personId} reload={reload}
                  onDone={line => setLines(l => ({ ...l, [card.key]: line }))} />
              )}
              {edits && card.cohort?.id && SURGERY_SLUGS.includes(card.product.slug) && (
                <SurgeryBox card={card} api={api} reload={reload} />
              )}
              {(edits && card.add) || lines[card.key] ? (
                <div className="adc-cohort-foot">
                  {edits && card.add && (
                    <Button size="sm" onClick={() => add(card)} busy={busy === card.key} disabled={Boolean(addedHere) || Boolean(busy)}>
                      {addedHere ? COPY.added : card.add.label}
                    </Button>
                  )}
                  {edits && isGroup(card) && <RemoveGroup card={card} api={api} data={data} reload={reload} onLine={setGroupLine} />}
                  {lines[card.key] && <span className={lines[card.key].bad ? 'ad-error' : 'adc-flash'} role={lines[card.key].bad ? 'alert' : 'status'}>{lines[card.key].text}</span>}
                </div>
              ) : null}
            </section>
            {edits && lastOf.get(card.product.id) === card.key && (
              <GroupsFoot product={card.product} data={data} api={api} personId={personId} reload={reload} line={groupLine} onLine={setGroupLine} />
            )}
            </Fragment>
          )
        })}
      </div>
      <p className="adc-footnote">{edits ? COPY.footnote : null}{edits ? ' ' : ''}{edits ? COPY.pressHint : COPY.pressHintRegisters}</p>
    </>
  )
}

// A mastermind group's latest Zoom link, for its later sessions with none (owner, admin): 1 write, and the line
// goes in the card's foot
function ZoomFillBox({ card, fill, api, personId, reload, onDone }) {
  const [busy, setBusy] = useState(false)
  async function go() {
    if (busy) return
    setBusy(true)
    try {
      const n = await fillZoomLinks(api, { cohortId: card.cohort.id, url: fill.url, personId, nowMs: Date.now() })
      await reload()
      onDone({ text: COPY.zoomFillDone(n, card.cohort.name) })
    } catch (e) {
      onDone({ bad: true, text: api.words(e) })
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="adc-wa adc-zoomfill">
      <span className="adc-wa-label">{COPY.zoomFillLabel}</span>
      <span className="adc-zoomfill-link">{fill.url}</span>
      <span className="adc-wa-note">{COPY.zoomFillNote(fill.later.length)}</span>
      <span><Button size="sm" className="adc-zoomfill-btn" busy={busy} onClick={go}>{COPY.zoomFill}</Button></span>
    </div>
  )
}

// The box under a session still to come: its date, UK time, length and Zoom link. A mastermind group's session
// with no date yet is offered the time of the group's last dated session; with no date picked, that time alone
// isn't saved.
function SessionBox({ row, card, api, personId, onClose, onSaved }) {
  const s = row.session
  const prior = !s.starts_at && card.product.type === 'mastermind' && card.cohort?.id
    ? card.rows.map(r => r.session).filter(x => x.starts_at && x.id !== s.id).sort((a, b) => Date.parse(b.starts_at) - Date.parse(a.starts_at))[0]
    : null
  const offered = prior ? hhmm(prior.starts_at) : ''
  const [day, setDay] = useState(s.starts_at ? ukDayOf(s.starts_at) : '')
  const [at, setAt] = useState(hhmm(s.starts_at) || offered)
  const [minutes, setMinutes] = useState(String(s.duration_minutes || card.product.default_session_minutes || 90))
  const [zoom, setZoom] = useState(s.zoom_join_url || '')
  const [problem, setProblem] = useState(null)
  const [busy, setBusy] = useState(false)
  const ids = useId()
  const startsAt = day && at ? ukToIso(day, at) : null

  async function save() {
    if (busy) return
    setProblem(null)
    if (day ? !at : (at && at !== offered)) return setProblem(COPY.pickDateAndTime)
    if (zoom.trim() && !/^https:\/\//i.test(zoom.trim())) return setProblem(COPY.zoomBad)
    setBusy(true)
    try {
      const saved = await saveSession(api, { session: s, startsAt, minutes: Number(minutes), zoom: zoom.trim(), personId })
      await onSaved(saved.starts_at ? COPY.sessionSaved(row.label, `${longDate(saved.starts_at)} at ${time(saved.starts_at)}`) : COPY.sessionSavedNoDate(row.label))
    } catch (e) {
      setProblem(api.words(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="adc-sbox" role="group" aria-labelledby={`${ids}-h`}>
      <span className="adc-sbox-title" id={`${ids}-h`}>{COPY.sessionBox(row.label, card.cohort.name)}</span>
      <div className="adc-sbox-grid">
        <DateField label={COPY.date} value={day} onChange={v => { setDay(v); setProblem(null) }} plain />
        <TimeField label={COPY.timeUk} value={at} onChange={v => { setAt(v || ''); setProblem(null) }} plain />
        <Field label={COPY.sessionLength} plain>
          {props => <Select {...props} value={minutes} onChange={setMinutes} options={lengthOptions(s.duration_minutes)} />}
        </Field>
        <Field label={COPY.zoomLink} plain>
          {props => <TextInput {...props} value={zoom} onChange={v => { setZoom(v); setProblem(null) }} placeholder={COPY.zoomPlaceholder} inputMode="url" autoComplete="off" />}
        </Field>
      </div>
      <div className="adc-sbox-foot">
        <Button kind="ink" size="sm" busy={busy} onClick={save}>{COPY.save}</Button>
        <Button size="sm" onClick={onClose} disabled={busy}>{COPY.cancel}</Button>
        {problem && <span className="ad-error" role="alert">{problem}</span>}
      </div>
    </div>
  )
}

// An intake's deal surgery (Profit On Your Terms): owner and admin switch it on or off. Its members then
// see Your deal surgery on the course's home, and a request goes on Today (data-model 10.6.2).
function SurgeryBox({ card, api, reload }) {
  const on = Boolean(card.cohort.deal_surgery)
  const [line, setLine] = useState(null)
  const [busy, setBusy] = useState(false)
  async function flip(next) {
    if (busy) return
    setBusy(true)
    setLine(null)
    try {
      await saveDealSurgery(api, { cohortId: card.cohort.id, on: next })
      await reload()
      setLine({ text: next ? COPY.surgeryOn(card.cohort.name) : COPY.surgeryOff(card.cohort.name) })
    } catch (e) {
      setLine({ bad: true, text: api.words(e) })
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="adc-surgery">
      <div className="adc-surgery-row">
        <span className="adc-wa-label">{COPY.surgery}</span>
        <Switch checked={on} onChange={flip} disabled={busy} label={`${COPY.surgery}, ${card.cohort.name}`} stateLabel={COPY.surgeryState(on)} />
      </div>
      <span className="adc-wa-note">{COPY.surgeryNote}</span>
      {line && <span className={line.bad ? 'ad-error' : 'adc-flash'} role={line.bad ? 'alert' : 'status'}>{line.text}</span>}
    </div>
  )
}

// An Accelerator group's WhatsApp invite link (cohort_links): owner and admin change it
function WhatsappBox({ card, api, personId, edits, reload }) {
  const saved = card.link?.whatsapp_invite_url || ''
  const [url, setUrl] = useState(saved)
  const [line, setLine] = useState(null)
  const [busy, setBusy] = useState(false)
  const ids = useId()
  async function save() {
    if (busy) return
    const v = url.trim()
    if (v && !/^https:\/\//i.test(v)) return setLine({ bad: true, text: COPY.whatsappBad })
    setBusy(true)
    try {
      await saveWhatsapp(api, { cohortId: card.cohort.id, url: v, personId })
      await reload()
      setLine({ text: COPY.whatsappSaved(card.cohort.name) })
    } catch (e) {
      setLine({ bad: true, text: api.words(e) })
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="adc-wa">
      <label className="adc-wa-label" htmlFor={`${ids}-wa`}>{COPY.whatsapp}</label>
      <div className="adc-wa-row">
        <input id={`${ids}-wa`} className="ad-input adc-wa-input" aria-label={COPY.whatsappAria} value={url} readOnly={!edits}
          onChange={e => { setUrl(e.target.value); setLine(null) }} autoComplete="off" spellCheck={false} />
        {edits && url.trim() !== saved && <Button size="sm" kind="ink" busy={busy} onClick={save}>{COPY.whatsappSave}</Button>}
      </div>
      <span className="adc-wa-note">{COPY.whatsappNote}</span>
      {line && <span className={line.bad ? 'ad-error' : 'adc-flash'} role={line.bad ? 'alert' : 'status'}>{line.text}</span>}
    </div>
  )
}
